// ArticleDetail.js

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
    Typography, 
    Container, 
    Paper, 
    Box, 
    Chip, 
    Divider, 
    Skeleton, 
    Alert, 
    IconButton, 
    Button, 
    Menu, 
    MenuItem,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { 
    CalendarToday, 
    Person, 
    Facebook, 
    Twitter, 
    LinkedIn,
    Language as LanguageIcon,
    ArrowDropDown as ArrowDropDownIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../api/axiosInstance'; // Make sure this path is correct

// Security: For safely rendering HTML content
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';


// --- Helper Components (defined in one file for simplicity) ---

const languageMap = {
    en: 'English',
    zh: '中文', // Mandarin
    hi: 'हिन्दी', // Hindi
    es: 'Español', // Spanish
    ar: 'العربية' // Arabic
};

const LanguageSwitcher = ({ currentLanguage, onLanguageChange, availableLangs = [] }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
    const open = Boolean(anchorEl);

    if (availableLangs.length <= 1) {
        return null; // Don't show if there's only one language option
    }

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleLanguageSelect = (langCode) => {
        onLanguageChange(langCode); // Call the function passed from the parent
        handleClose();
    };
    
    // Desktop view: Simple buttons
    if (isDesktop) {
        return (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <LanguageIcon sx={{ color: 'text.secondary', [currentLanguage === 'ar' ? 'ml' : 'mr']: 0.5 }} />
                {availableLangs.map((code) => (
                    <Button
                        key={code}
                        size="small"
                        variant={currentLanguage === code ? 'contained' : 'text'}
                        onClick={() => handleLanguageSelect(code)}
                    >
                        {languageMap[code]}
                    </Button>
                ))}
            </Box>
        );
    }
    
    // Mobile view: Compact dropdown menu
    return (
        <Box>
            <Button
                id="language-button"
                aria-controls={open ? 'language-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                variant="outlined"
                size="small"
                sx={{ textTransform: 'none' }}
                startIcon={<LanguageIcon />}
                endIcon={<ArrowDropDownIcon />}
            >
                {languageMap[currentLanguage]}
            </Button>
            <Menu
                id="language-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{ 'aria-labelledby': 'language-button' }}
            >
                {availableLangs.map((code) => (
                    <MenuItem
                        key={code}
                        selected={code === currentLanguage}
                        onClick={() => handleLanguageSelect(code)}
                    >
                        {languageMap[code]}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};

const ArticleSkeleton = () => (
  <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
    <Paper elevation={3} sx={{ p: { xs: 2, md: 4 } }}>
      <Skeleton variant="text" sx={{ fontSize: '2.5rem', width: '80%' }} />
      <Skeleton variant="text" sx={{ fontSize: '1rem', width: '40%', mb: 2 }} />
      <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 1, mb: 3 }} />
      <Skeleton variant="text" />
      <Skeleton variant="text" />
      <Skeleton variant="text" width="60%" />
    </Paper>
  </Container>
);

const Metadata = ({ article, language }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 3, color: 'var(--color-text-secondary)', my: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Person sx={{ fontSize: 20, [language === 'ar' ? 'ml' : 'mr']: 1 }} />
            <Typography variant="body2" sx={{ mt: '5px' }}>{article.author || 'Anonymous'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CalendarToday sx={{ fontSize: 20, [language === 'ar' ? 'ml' : 'mr']: 1 }} />
            <Typography variant="body2" sx={{ mt: '5px' }}>
                {new Date(article.publishedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <VisibilityIcon sx={{ fontSize: 20, [language === 'ar' ? 'ml' : 'mr']: 1 }} />
            <Typography variant="body2" sx={{ mt: '5px' }}>{article.views?.toLocaleString() || '0'} views</Typography>
        </Box>
    </Box>
);

const ShareButtons = ({ articleTitle, language }) => {
    const articleUrl = window.location.href;
    const title = encodeURIComponent(articleTitle);

    return (
        <Box sx={{ mt: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
             <Typography variant="subtitle2" sx={{ [language === 'ar' ? 'ml' : 'mr']: 1 }}>Share:</Typography>
             <IconButton aria-label="Share on Facebook" href={`https://www.facebook.com/sharer/sharer.php?u=${articleUrl}`} target="_blank" rel="noopener noreferrer">
                 <Facebook />
             </IconButton>
             <IconButton aria-label="Share on Twitter" href={`https://twitter.com/intent/tweet?url=${articleUrl}&text=${title}`} target="_blank" rel="noopener noreferrer">
                 <Twitter />
             </IconButton>
             <IconButton aria-label="Share on LinkedIn" href={`https://www.linkedin.com/shareArticle?mini=true&url=${articleUrl}&title=${title}`} target="_blank" rel="noopener noreferrer">
                 <LinkedIn />
             </IconButton>
        </Box>
    )
};


// --- Main Component ---

function ArticleDetail() {
  const { slug } = useParams();
  const location = useLocation();

  // Get initial language from URL or default to 'en'
  const getInitialLanguage = useCallback(() => {
    const params = new URLSearchParams(location.search);
    return params.get('lang') || 'en';
  }, [location.search]);

  const [language, setLanguage] = useState(getInitialLanguage);

  // Update language if the URL parameter changes (e.g., browser back/forward)
  useEffect(() => {
    setLanguage(getInitialLanguage());
  }, [getInitialLanguage]);

  // Fetching data using React Query - only once per article
  const { data: article, isLoading, isError, error } = useQuery({
    queryKey: ['article', slug], // Query key does not include language anymore
    queryFn: async () => {
      // API call no longer needs the language parameter
      const { data } = await axiosInstance.get(`/api/immigration-articles/${slug}`);
      return data;
    },
    staleTime: Infinity, // Cache the article data indefinitely
  });

  useEffect(() => {
    const incrementViewCount = async () => {
      try {
        await axiosInstance.post(`/api/immigration-articles/${slug}/view`);
      } catch (e) {
        // silently fail
      }
    };
    incrementViewCount();
  }, [slug]);

  if (isLoading) {
    return <ArticleSkeleton />;
  }

  // Handle network or server errors first
  if (isError) {
    const status = error.response?.status;
    const message = status === 404
      ? "Sorry, we couldn't find the article you're looking for."
      : "An unexpected error occurred. Please try again later.";
      
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{message}</Alert>
      </Container>
    );
  }

  // Then, handle missing data or translations
  const translationsMap = article?.translations ? new Map(Object.entries(article.translations)) : new Map();
  const currentTranslation = translationsMap.get(language) || translationsMap.get('en');

  if (!article || !currentTranslation) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">The article content could not be loaded. It might be incomplete or unavailable.</Alert>
      </Container>
    );
  }

  const availableLangs = Array.from(translationsMap.keys());

  // Sanitize the HTML content to prevent XSS attacks
  const cleanHtml = DOMPurify.sanitize(currentTranslation.content || '');

  return (
    <Container maxWidth="md" sx={{ my: 4 }}>
      <Paper
        elevation={0}
        className="myCard"
        dir={language === 'ar' ? 'rtl' : 'ltr'}
        sx={{
            p: { xs: 2, sm: 3, md: 5 },
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderTop: '3px solid var(--color-accent-red)',
            borderRadius: 'var(--border-radius, 4px)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            '&:hover': {
                transform: 'none',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap-reverse', gap: 2 }}>
            <Box>
                 <Typography variant="overline" sx={{ color: 'var(--color-accent-red)', fontWeight: 'bold' }}>{article.category}</Typography>
                 <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700, mt: 1 }}>
                   {currentTranslation.title}
                 </Typography>
            </Box>
            <Box sx={{ pt: 1 }}>
                <LanguageSwitcher
                  currentLanguage={language}
                  onLanguageChange={setLanguage}
                  availableLangs={availableLangs}
                />
            </Box>
        </Box>

        <Metadata article={article} language={language} />
        
        {article.imageUrl && (
          <Box
            component="img"
            src={article.imageUrl}
            alt={currentTranslation.title}
            sx={{
              width: '100%',
              maxHeight: '450px',
              objectFit: 'cover',
              borderRadius: 2,
              my: 3,
            }}
          />
        )}

        {article.tags && article.tags.length > 0 && (
          <Box sx={{ my: 2 }}>
            {article.tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                sx={{
                    [language === 'ar' ? 'ml' : 'mr']: 1,
                    mb: 1,
                    backgroundColor: 'var(--color-primary)',
                    color: 'var(--color-text-on-primary)',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: 'var(--color-primary-dark)'
                    }
                }}
              />
            ))}
          </Box>
        )}
        
        <Divider sx={{ my: 3 }} />

        {/* Securely render sanitized HTML content */}
        <Box
          className="article-content"
          sx={{
            textAlign: language === 'ar' ? 'right' : 'left',
            '& p, & li': { lineHeight: 1.7, fontSize: '1.1rem', color: 'var(--color-text-primary)' },
            '& p': { mb: '1em' },
            '& ul, & ol': { [language === 'ar' ? 'pr' : 'pl']: 4, mb: '1em' },
            '& a': { color: 'var(--color-link)', textDecoration: 'underline' },
            '& h2, & h3, & h4': { mt: 4, mb: 2, fontWeight: 'bold', color: 'var(--color-primary)' },
            '& strong': { color: 'var(--color-text-primary)' },
            '& blockquote': {
                [language === 'ar' ? 'borderRight' : 'borderLeft']: '4px solid',
                borderColor: 'var(--color-border)',
                [language === 'ar' ? 'pr' : 'pl']: 2,
                m: 0,
                my: 2,
                fontStyle: 'italic',
                color: 'var(--color-text-secondary)'
            },
           '& table': {
             display: 'block',
             overflowX: 'auto',
             whiteSpace: 'nowrap',
             borderCollapse: 'collapse',
             width: '100%',
             my: 2,
             border: '1px solid var(--color-border)',
            },
           '& th, & td': {
               border: '1px solid var(--color-border)',
               p: 1,
               textAlign: language === 'ar' ? 'right' : 'left',
           },
           '& th': {
               backgroundColor: 'var(--color-background)',
               fontWeight: 'bold',
               color: 'var(--color-text-primary)',
           }
          }}
        >
          {parse(cleanHtml)}
        </Box>
        
        <Divider sx={{ my: 4 }} />
        
        <ShareButtons articleTitle={currentTranslation.title} language={language} />

      </Paper>
    </Container>
  );
}

export default ArticleDetail;
